import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";

const FIELDS = [["alias", "Alias"], ["cbu", "CBU/CVU (22 números)"], ["holder", "Titular de la cuenta"], ["cuit", "CUIT"], ["bank", "Banco"]];
const fmt = (s) => (s ? new Date(s).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "");

const Settings = () => {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/payments/transfer").then((r) => setF(r.data ?? {})).catch((e) => toast.error(apiError(e))); }, []);
  if (!f) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async (enabled) => {
    try { const { data } = await api.put("/admin/payments/transfer", { ...f, enabled }); setF(data); toast.success(enabled ? "Pago por transferencia activo en la web" : "Pago por transferencia desactivado"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(true); }} data-testid="transfer-settings" className="grid gap-3 rounded-3xl bg-white p-5 sm:grid-cols-2">
      <p className="text-sm font-bold sm:col-span-2">Datos de la cuenta {f.enabled ? <span data-testid="transfer-status" className="ml-2 rounded-full bg-[var(--lime)] px-3 py-1 text-xs">Activo</span> : <span data-testid="transfer-status" className="ml-2 rounded-full bg-black/5 px-3 py-1 text-xs">Desactivado</span>}</p>
      {FIELDS.map(([k, l]) => (
        <label key={k} className="text-sm"><span className="mb-1 block text-[var(--muted-ink)]">{l}</span>
          <Input data-testid={`transfer-input-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 rounded-xl" inputMode={k === "cbu" ? "numeric" : undefined} maxLength={k === "cbu" ? 22 : 80} />
        </label>
      ))}
      <div className="flex flex-wrap gap-2 sm:col-span-2">
        <button data-testid="transfer-save" className="h-11 rounded-xl bg-[var(--forest)] px-6 font-bold text-white">Guardar y activar</button>
        {f.enabled && <button type="button" onClick={() => save(false)} data-testid="transfer-disable" className="h-11 rounded-xl bg-black/5 px-6 font-bold">Desactivar</button>}
      </div>
    </form>
  );
};

const Pending = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/payments/transfer/pending").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(apiError(e)); setList([]); });
  useEffect(() => { load(); }, []);
  const confirm = async (b) => {
    if (!window.confirm(`¿Confirmar que se acreditó ${formatARS(b.total)} de ${b.code}?`)) return;
    try { await api.post(`/admin/bookings/${b.id}/charge`, { payment: "transferencia" }); toast.success(`${b.code} pagado`); load(); }
    catch (err) { toast.error(apiError(err)); load(); }
  };
  const view = async (b) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (w) w.location.href = url; else window.location.assign(url);
    } catch (err) { if (w) w.close(); toast.error(apiError(err)); }
  };
  if (!list) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!list.length) return <p data-testid="transfer-pending-empty" className="text-sm text-[var(--muted-ink)]">No hay transferencias esperando acreditación.</p>;
  return list.map((b) => (
    <div key={b.id} data-testid={`transfer-pending-${b.code}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-4 text-sm">
      <p><b className="font-mono">{b.code}</b> · {b.name} · {b.origin} → {b.destination} {b.date} {b.departure} · <b>{formatARS(b.total)}</b> · vence {fmt(b.expires_at)}
        {b.receipt_at ? <span data-testid={`transfer-receipt-badge-${b.code}`} className="ml-2 rounded-full bg-[var(--lime)] px-2 py-0.5 text-xs font-bold">Comprobante {fmt(b.receipt_at)}</span> : <span className="ml-2 rounded-full bg-black/5 px-2 py-0.5 text-xs">Sin comprobante</span>}</p>
      <div className="flex flex-wrap gap-2">
        {b.receipt_at && <button onClick={() => view(b)} data-testid={`transfer-receipt-view-${b.code}`} className="rounded-full bg-black/5 px-4 py-1.5 font-bold">Ver comprobante</button>}
        <button onClick={() => confirm(b)} data-testid={`transfer-confirm-${b.code}`} className="rounded-full bg-[var(--lime)] px-4 py-1.5 font-bold">Se acreditó: confirmar</button>
      </div>
    </div>
  ));
};

export const TransferAdmin = () => (
  <div data-testid="report-transfer" className="space-y-4">
    <Settings />
    <p className="pt-2 font-bold">Transferencias a confirmar</p>
    <p className="text-xs text-[var(--muted-ink)]">Buscá el código en el concepto de la transferencia. Al confirmar, el pasaje queda pagado y el pasajero recibe el aviso.</p>
    <Pending />
  </div>
);
